import { textOfStoredTurn } from './turn-recovery';
/* Shared helpers for listing staff (teacher/admin) threads and their messages. */
/* Both adminChat.ts and teacherChat.ts perform identical Supabase queries; */
/* only the return types differ. This module provides the runtime-shared core */
/* so type-specific wrappers stay thin. */

import { api } from './api';
import { WIDGET_TOOL_NAMES } from './sse';

export interface ThreadSummary {
  id: string;
  title: string | null;
  last_message_at: string | null;
}

/** Generic tool call shape used internally; callers re-cast to their own types. */
export interface RawToolCall {
  name: string;
  args?: Record<string, unknown>;
  refs?: unknown[];
}

export interface RawWidget {
  name: string;
  html: string;
  title: string | null;
}

export interface RawAttachment {
  url: string;
  mime: string;
  storagePath?: string;
}

export interface RawMsg {
  role: 'user' | 'assistant';
  content: string;
  tools?: RawToolCall[];
  widgets?: RawWidget[];
  attachments?: RawAttachment[];
  failed?: boolean;
  clientSendId?: string;
}

/**
 * List threads for the current user filtered by mode ('teacher' | 'admin').
 * Ordered by last_message_at desc, capped at 50.
 */
export async function listStaffThreads(mode: string): Promise<ThreadSummary[]> {
  /* CLEAN-ARCH (Vague 4) : via le port tRPC (scopé au caller server-side) au lieu */
  /* de supabase-js direct. On ne garde que les champs du résumé. */
  const rows = await api.threads.list.query({ mode, limit: 50 });
  return rows.map((t) => ({ id: t.id, title: t.title, last_message_at: t.last_message_at }));
}

/**
 * Load messages for a thread, splitting metadata tool calls into
 * widget entries (html-bearing) and non-widget tool chips.
 */
export async function listStaffMessages(threadId: string): Promise<RawMsg[]> {
  /* CLEAN-ARCH (Vague 4) : lecture via le port tRPC (ownership server-side) ; */
  /* la transformation metadata → tools/widgets reste ici (présentation data). */
  const data = await api.messages.list.query({ threadId });
  return (
    data as Array<{
      role: string;
      transcript: string;
      metadata: { tools?: RawToolCall[]; clientSendId?: string } | null;
      attachments?: RawAttachment[] | null;
      turn_failed?: boolean;
    }>
  ).map((m) => {
    const allTools = m.metadata?.tools ?? [];
    const widgets: RawWidget[] = allTools
      .filter((t) => WIDGET_TOOL_NAMES.has(t.name) && typeof t.args?.html === 'string')
      .map((t) => ({ name: t.name, html: String(t.args?.html ?? ''), title: (t.args?.title as string) ?? null }));
    const tools = allTools.filter((t) => !WIDGET_TOOL_NAMES.has(t.name));
    const attachments = m.attachments ?? [];
    return {
      role: m.role === 'assistant' ? 'assistant' : 'user',
      content: textOfStoredTurn(m.transcript, m.turn_failed),
      tools: tools.length ? tools : undefined,
      widgets: widgets.length ? widgets : undefined,
      attachments: attachments.length ? attachments : undefined,
      failed: m.turn_failed || undefined,
      clientSendId: m.metadata?.clientSendId,
    } as RawMsg;
  });
}
